<!-- 
  * SharePointSelectorModal Component
  * 
  * Hierarchical SharePoint browser with two modes:
  * 1. **File Selection** (used by InputWidget.vue) - Select one or more files from SharePoint for a document field in a table
  * 2. **Location Configuration** (used by FieldModal.vue) - Set default storage location for a document field
  * 
  * Navigation: Site Entry → Document Libraries → Folders → Files
  * Uses Microsoft Graph API via sharePointUtils to fetch SharePoint content.
-->
<template>
  <b-modal
    id="sharePointSelectorModal"
    :title="title"
    visible
    scrollable
    body-class="sharepoint-modal-body"

    @ok="handleSelect"
    @hidden="$emit('hidden')"
  >
    <content-spinner :show="loading" />
    
    <div v-if="!loading">
      <show-error 
        v-if="errorMessage" 
        id="errorMessage"
        :show="!!errorMessage" 
        :text="errorMessage"  
      />
      
      <!-- Site name input if no site is loaded -->
      <div 
        v-if="!currentSite" 
        class="mb-3"
      >
        <label for="siteUrlInput">Enter SharePoint Site URL:</label>
        <b-input-group>
          <b-form-input
            id="siteUrlInput"
            v-model="siteUrlInput"
            placeholder="e.g. https://contoso.sharepoint.com/sites/SiteName"
            type="url"
            autocomplete="off"
            @keyup.enter="loadSite()"
          />
          <b-input-group-append>
            <b-button 
              variant="primary" 
              :disabled="!isValidSharePointUrl(siteUrlInput)"
              :title="!isValidSharePointUrl(siteUrlInput) ? 'Please enter a valid SharePoint site URL' : ''"
              @click="loadSite()"
            >
              Load Site
            </b-button>
          </b-input-group-append>
        </b-input-group>
      </div>

      <!-- Site selection bar with change site option -->
      <div 
        v-if="currentSite" 
        id="location-item-site"
        class="location-item d-flex justify-content-between align-items-center mb-2 p-2 border rounded cursor-pointer"
        :class="selectedLocation && selectedLocation.id === currentSite.id ? 'bg-primary text-white' : 'bg-light'"
        @click="selectItem(currentSite)"
      >
        <div class="d-flex align-items-center">
          <font-awesome-icon 
            :icon="['fas', 'globe']" 
            class="mr-2"
            :class="selectedLocation && selectedLocation.id === currentSite.id ? 'text-white' : 'text-primary'"
          />
          <div>
            {{ currentSite.displayName || currentSite.name || siteUrlInput }}
          </div>
        </div>
        <div class="d-flex align-items-center">
          <b-button 
            :variant="selectedLocation && selectedLocation.id === currentSite.id ? 'outline-light': 'outline-secondary'" 
            size="sm"
            @click.stop="resetAllState"
          >
            Change Site
          </b-button>
        </div>
      </div>

      <!-- File search and filter component -->
      <FileSearchFilter
        v-if="currentSite && isSelectingFiles"
        :files="currentFiles"
        :site-id="currentSite.id"
        :enable-site-search="true"
        :current-location-name="currentLocationDisplayName"
        @files-filtered="onFilesFiltered"
      />

      <!-- Breadcrumb navigation for tracking current location in hierarchy -->
      <b-breadcrumb 
        v-if="currentSite" 
        class="mb-2"
      >
        <b-breadcrumb-item 
          :active="breadcrumbs.length === 0"
          @click="navigateToSiteLevel"
        >
          Document Libraries
        </b-breadcrumb-item>
        <b-breadcrumb-item
          v-for="(crumb, index) in breadcrumbs"
          :active="index === breadcrumbs.length - 1"
          @click="navigateToBreadcrumb(index)"
        >
          {{ crumb.name }}
        </b-breadcrumb-item>
      </b-breadcrumb>

      <div v-if="currentSite && currentItems.length > 0">
        <!-- Explain click and double-click behavior to users -->
        <div class="text-muted small mb-2">
          Tip: Single-click to select, double-click to open folders or drives.
        </div>
        <!-- Select All checkbox for files when in file selecting mode -->
        <div 
          v-if="isSelectingFiles && hasFiles" 
          class="mb-3 p-2 border-bottom"
        >
          <b-form-checkbox
            :checked="areAllFilesSelected"
            :indeterminate="areSomeFilesSelected && !areAllFilesSelected"
            @change="toggleSelectAll"
          >
            <strong>
              Select All Files ({{ displayFiles.length }} {{ displayFiles.length > 1 ? 'files' : 'file' }}{{ fileSearchActive ? ' shown' : '' }})
              <span 
                v-if="isSiteSearch" 
                class="text-primary small ml-1"
              >
                (from site search)
              </span>
            </strong>
          </b-form-checkbox>
        </div>

        <!-- Location items list - displays drives or folders or files based on current navigation level -->
        <div 
          v-for="(item, index) in displayItems" 
          :id="`location-item-${index}`"
          class="location-item p-2 mb-2 border rounded cursor-pointer"
          :class="getItemSelectionClass(item)"
          @click="selectItem(item)"
          @dblclick="navigateToItem(item)"
        >
          <div class="d-flex align-items-center">
            <!-- Checkbox appears when at least one file is selected -->
            <div>
              <b-form-checkbox
                :class="isSelectingFiles && item.type === 'file' && selectedFiles.length > 0 ? '' : 'invisible'"
                :checked="isFileSelected(item)"
                @click.stop
                @change="toggleFileSelection(item)"
              />
            </div>
            <font-awesome-icon 
              :icon="getItemIcon(item)" 
              class="mr-3"
              :class="selectedLocation && selectedLocation.id === item.id ? 'text-white' : item.type !== 'file' ? 'text-primary' : ''"
              :style="getItemIconStyle(item)"
              size="lg"
            />
            <div>
              {{ item.displayName || item.name }}
            </div>
          </div>
        </div>
      </div>

      <!-- Empty state when no items are found at current level -->
      <div
        v-else-if="currentSite"
        class="text-center text-muted py-4"
      >
        <font-awesome-icon
          :icon="['fas', 'folder-open']"
          size="3x"
          class="mb-3"
        />
        <p>{{ isSelectingFiles ? 'This folder is empty' : 'No folders found in this location' }}</p>
      </div>
    </div>

    <template #modal-footer="{ ok, cancel }">
      <div class="d-flex justify-content-between align-items-center w-100">
        <!-- Selected files count for multi-select mode -->
        <div 
          v-if="isSelectingFiles && selectedFiles.length > 0" 
          class="text-muted"
        >
          {{ selectedFiles.length }} file{{ selectedFiles.length > 1 ? 's' : '' }} selected
        </div>
        <div v-else />
        
        <div>
          <b-button
            variant="secondary"
            class="mr-2"
            @click="cancel()"
          >
            Cancel
          </b-button>
          <b-button 
            v-if="currentSite"
            variant="primary" 
            :disabled="isSelectingFiles ? selectedFiles.length === 0 : !selectedLocation"
            :title="isSelectingFiles && selectedFiles.length === 0 ? 'Please select at least one file' : !isSelectingFiles && !selectedLocation ? 'Please select a default location' : ''"
            @click="ok()"
          >
            Select
          </b-button>
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script>
import * as SharePointUtils from '@/core/utils/sharePointUtils';
import { getFileTypeIcon, getFileTypeColour } from '@/core/utils/fileTypeUtils';
import {v4 as uuidv4} from 'uuid';
// Components
import ContentSpinner from '@/core/components/ContentSpinner';
import ShowError from '@/core/components/ShowError.vue';
import FileSearchFilter from './FileSearchFilter.vue';

export default {
  name: 'SharePointSelectorModal',
  components: {
    ContentSpinner,
    ShowError,
    FileSearchFilter,
  },
  props: {
    initialLocationData: {
      type: [Object, String],
      default: null,
    },
    isSelectingFiles: {
      type: Boolean,
      default: false, 
    },
    title: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      errorMessage: '',
      currentItems: [],
      selectedLocation: null,
      selectedFiles: [], 
      breadcrumbs: [],
      siteUrlInput: '',
      currentSite: null,
      localInitialLocationData: this.parseInitialLocationData(this.initialLocationData),
      filteredFiles: [],
      fileSearchActive: false,
      isSiteSearch: false,
      isSearching: false,
      searchError: null,
    };
  },
  watch: {
    // Reset file search when navigating to different folders
    currentItems() {
      // Only reset if not doing site search
      if (!this.isSiteSearch) {
        this.filteredFiles = [];
        this.fileSearchActive = false;
      }
    },
  },
  computed: {
    // Check if there are any files in the current items
    hasFiles() {
      return this.currentItems.some(item => item.type === 'file');
    },
    // Check if all files are selected
    areAllFilesSelected() {
      const files = this.displayFiles;
      return files.length > 0 && files.every(file => this.isFileSelected(file));
    },
    // Check if some files are selected (for indeterminate checkbox state)
    areSomeFilesSelected() {
      const files = this.displayFiles;
      return files.some(file => this.isFileSelected(file));
    },
    // Get files from current items for search/filter
    currentFiles() {
      return this.currentItems.filter(item => item.type === 'file');
    },
    // Get non-file items (folders, drives) from current items
    currentFoldersAndDrives() {
      return this.currentItems.filter(item => item.type !== 'file');
    },
    // Get files to display (filtered if search is active, otherwise all files)
    displayFiles() {
      return this.fileSearchActive ? this.filteredFiles : this.currentFiles;
    },
    // Get items to display in the list (folders/drives + filtered files)
    displayItems() {
      // If there's an active search, only show files (hide folders/drives)
      if (this.fileSearchActive) {
        return this.displayFiles;
      }
      // Otherwise show both folders/drives and files
      return [...this.currentFoldersAndDrives, ...this.displayFiles];
    },
    // Get current location display name for search context
    currentLocationDisplayName() {
      if (this.breadcrumbs.length > 0) {
        const lastBreadcrumb = this.breadcrumbs[this.breadcrumbs.length - 1];
        return lastBreadcrumb.name;
      }
      return 'Document Libraries';
    },
  },
  methods: {
    /**
     * Clear search and file selection state
     * Used when navigating to different locations
     */
    clearSearchAndSelectionState() {
      if (this.isSelectingFiles) {
        this.selectedFiles = [];
        this.filteredFiles = [];
        this.fileSearchActive = false;
        this.isSiteSearch = false;
      }
    },
    // Handle errors with consistent formatting and fallback messages
    handleError(error, fallbackMessage = 'An error occurred') {
      this.errorMessage = error.message || fallbackMessage;
    },
    // Execute async operation with loading and error handling
    async executeWithLoadingAndErrorHandling(operation, errorMessage) {
      this.loading = true;
      try {
        await operation();
      } catch (error) {
        this.handleError(error, errorMessage);
      } finally {
        this.loading = false;
      }
    },
    // Reset all state for site change
    resetAllState() {
      this.currentSite = null;
      this.currentItems = [];
      this.filteredFiles = [];
      this.fileSearchActive = false;
      this.isSiteSearch = false;
      this.isSearching = false;
      this.searchError = null;
      this.selectedLocation = null;
      this.selectedFiles = [];
      this.breadcrumbs = [];
      this.errorMessage = '';
      this.siteUrlInput = '';
      this.localInitialLocationData = null;
    },
    // Format selected files to emit to parent
    formatSelectedFilesForEmission(files) {
      return files.map(file => ({
        id: file.id,
        name: file.name || file.displayName,
        webUrl: file.webUrl,
        driveId: file.parentReference.driveId,
        DocumentType: 'SharePoint',
        attachmentGuid: uuidv4(), // Generate a random UUID for each file
      }));
    },
    /**
     * Parse initial location data from JSON string or return as is if already an object
     * @param {string|Object|null} data - The data to parse
     * @returns {Object|Array|null} Parsed data
     */
    parseInitialLocationData(data) {
      if (!data) return null;
      
      // If it's already an object/array, return as is
      if (typeof data === 'object') {
        return data;
      }
      // If it's a string, try to parse it as JSON
      if (typeof data === 'string') {
        try {
          const parsed = JSON.parse(data);
          return parsed;
        } catch (error) {
          return null;
        }
      }
      return null;
    },
    /**
     * Load a SharePoint site and drives by name/URL or ID
     * @param {string|null} defaultSiteId - Optional site ID to load directly if default site ID already exists
     * Otherwise it will use the siteUrlInput value provided by the user
     */
    async loadSite(defaultSiteId = null) {      
      this.loading = true;
      this.errorMessage = '';
      this.searchError = null;
      
      try {
        let siteData;
        if (defaultSiteId) {
          siteData = await SharePointUtils.getSiteById(defaultSiteId);
        } else if (this.siteUrlInput.trim()) {
          if (!this.isValidSharePointUrl(this.siteUrlInput)) {
            throw new Error('Please enter a valid SharePoint site URL (e.g. https://contoso.sharepoint.com/sites/SiteName)');
          }
          siteData = await SharePointUtils.getSite(this.siteUrlInput);
        }

        if (siteData) {
          this.currentSite = {
            ...siteData,
            type: 'site',
          };

          // If default site exists only, set it as selected location
          if (this.localInitialLocationData && 
              this.localInitialLocationData.SPDefaultSiteId && 
              !this.localInitialLocationData.SPDefaultDriveId && 
              !this.isSelectingFiles) {
            this.selectedLocation = this.currentSite;
          }

          // Load drives for this site
          await this.loadSiteDrives(this.currentSite.id);
        
          // If default drive exists, set it as selected location
          if (this.localInitialLocationData && 
              this.localInitialLocationData.SPDefaultDriveId && 
              !this.localInitialLocationData.SPDefaultFolderId &&
              !this.isSelectingFiles) {
            this.selectedLocation = this.currentItems.find(d => d.id === this.localInitialLocationData.SPDefaultDriveId) || null;
          }

          // If default folder exists, navigate to it
          if ((this.localInitialLocationData && 
               this.localInitialLocationData.SPDefaultDriveId && 
               this.localInitialLocationData.SPDefaultFolderId) || 
              (this.localInitialLocationData && 
               this.localInitialLocationData.SPDefaultDriveId && 
               this.isSelectingFiles)) {
            await this.navigateToInitialDrive(this.localInitialLocationData.SPDefaultDriveId);
          }

          // If default folder exists, navigate to it using the folder path
          if ((this.localInitialLocationData && 
               this.localInitialLocationData.SPDefaultFolderId && 
               this.localInitialLocationData.SPDefaultFolderPath) || 
              (this.localInitialLocationData && 
               this.isSelectingFiles && 
               this.localInitialLocationData.SPDefaultFolderId)) {
            await this.navigateToFolderByPath(this.localInitialLocationData.SPDefaultDriveId, this.localInitialLocationData.SPDefaultFolderPath, this.localInitialLocationData.SPDefaultFolderId);

            if (this.selectedLocation && this.isSelectingFiles) {
              await this.loadFolders(this.selectedLocation.driveId, this.selectedLocation.id);
              this.breadcrumbs.push({ name: this.selectedLocation.name, item: this.selectedLocation });
            }
          } 
        } 
      } catch (error) {
        this.handleError(error);
      } finally {
        this.loading = false;
      }
    },
    // Validate if the input is a valid SharePoint site URL
    isValidSharePointUrl(url) {
      if (!url) return false;
      const cleanInput = url.trim();
      try {
        const parsed = new URL(cleanInput);
        return parsed.protocol === 'https:' || parsed.protocol === 'http:';
      } catch {
        return false;
      }
    },
    // Load document libraries for a specific SharePoint site
    async loadSiteDrives(siteId) {
      const drives = await SharePointUtils.getSiteDrives(siteId);
      if (drives) {
        this.currentItems = drives.map(drive => ({
          ...drive,
          type: 'drive',
          siteId: siteId,
        }));
      }
    },
    /**
     * Load folders within a specific drive or folder
     * @param {string} driveId - ID of the document library/drive
     * @param {string|null} folderId - ID of parent folder, null for root
     */
    async loadFolders(driveId, folderId = null) {
      const items = await SharePointUtils.getItems(driveId, folderId);
      const flatItems = items.flat(); // Flatten the items array (as API call returns nested arrays)
      this.currentItems = flatItems
        .filter(item => this.isSelectingFiles ? true : item.folder) // Filter to only show folders when not selecting files
        .map(item => ({
          ...item,
          type: item.folder ? 'folder' : 'file',
          driveId: driveId,
          folderId: folderId || null,
          siteId: item.parentReference?.siteId || null,
        }));
    },
    /**
     * Navigate to the initial drive specified in initialLocationData
     * @param {string} driveId - The drive ID to navigate to
     */
    async navigateToInitialDrive(driveId) {
      try {
        // Find the drive in currentItems
        const drive = this.currentItems.find(item => item.id === driveId);
        if (drive) {
          // Navigate into the drive
          this.breadcrumbs.push({ name: drive.name, item: drive });
          await this.loadFolders(drive.id);
        }
      } catch (error) {
        this.handleError(error, 'Failed to navigate to initial drive');
      }
    },
    /**
     * Navigate to the initial folder specified in initialLoationData using the folder path
     * @param {string} driveId - The drive ID containing the folder
     * @param {string} folderPath - The full path to the folder (e.g., "root:/Folder1/Subfolder2")
     * @param {string} targetFolderId - The ID of the target folder
     */
    async navigateToFolderByPath(driveId, folderPath, targetFolderId) {
      try {
        const rootIndex = folderPath.indexOf('root:/');
        
        // If root:/ is not found but it contains 'root:' then it is not a nested folder and the selected location is in the root
        if (rootIndex === -1 && folderPath.includes('root:')) {
          this.selectedLocation = this.currentItems.find(f => f.id === this.localInitialLocationData.SPDefaultFolderId) || null;
        }
        // If root:/ is found, navigate through the path
        else if (rootIndex !== -1) {
          // Get the part after "root:/" and split by "/"
          let pathSegments = [];
          const pathAfterRoot = folderPath.substring(rootIndex + 6); // 6 = length of "root:/"
          pathSegments = pathAfterRoot.split('/').filter(segment => segment.trim() !== '');

          let currentFolderId = null;
          // Navigate through each folder in the path
          for (let i = 0; i < pathSegments.length; i++) {
            const segmentName = pathSegments[i];
          
            // Find the folder with the matching name
            const targetFolder = this.currentItems.find(item => 
              item.name === segmentName || item.displayName === segmentName,
            );

            if (targetFolder) {
            // Add to breadcrumbs
              this.breadcrumbs.push({ name: targetFolder.name, item: targetFolder });
              currentFolderId = targetFolder.id;
              await this.loadFolders(driveId, currentFolderId);
              // If this is the last segment, select this folder
              if (i === pathSegments.length - 1) {
                this.selectedLocation = this.currentItems.find(f => f.id === targetFolderId) || targetFolder;
              }
            }
          }
        }
      } catch (error) {
        this.handleError(error, 'Failed to navigate to folder path');
      }
    },
    // Navigate into an item based on its type
    async navigateToItem(item) {
      if (item.type === 'drive') {
        await this.navigateToDrive(item);
      } else if (item.type === 'folder') {
        await this.navigateToFolder(item);
      }
    },
    /**
     * Navigate back to the site level (showing site + drives)
     */
    async navigateToSiteLevel() {
      if (!this.currentSite) return;
      
      await this.executeWithLoadingAndErrorHandling(async () => {
        this.breadcrumbs = [];
        await this.loadSiteDrives(this.currentSite.id);
        this.selectedLocation = null;
        this.clearSearchAndSelectionState();
      }, 'Failed to load site drives');
    },
    // Navigate into a document library to show its folders
    async navigateToDrive(drive) {
      await this.executeWithLoadingAndErrorHandling(async () => {
        // Add drive to breadcrumb trail
        this.breadcrumbs.push({ name: drive.name, item: drive });
        await this.loadFolders(drive.id);
        this.selectedLocation = null;
        this.clearSearchAndSelectionState();
      }, 'Failed to load drive contents');
    },
    // Navigate into a folder to show its subfolders
    async navigateToFolder(folder) {
      await this.executeWithLoadingAndErrorHandling(async () => {
        // Add folder to breadcrumb trail
        this.breadcrumbs.push({ name: folder.name, item: folder });
        await this.loadFolders(folder.driveId, folder.id);
        this.selectedLocation = null;
        this.clearSearchAndSelectionState();
      }, 'Failed to load folder contents');
    },
    /**
     * Navigate to a specific breadcrumb level
     * Truncates breadcrumbs and reloads content for that level
     * @param {number} index - Index of breadcrumb to navigate to
     */
    async navigateToBreadcrumb(index) {
      await this.executeWithLoadingAndErrorHandling(async () => {
        const crumb = this.breadcrumbs[index];
        this.breadcrumbs = this.breadcrumbs.slice(0, index + 1);
        if (crumb.item.type === 'drive') {
          await this.loadFolders(crumb.item.id);
        } else if (crumb.item.type === 'folder') {
          await this.loadFolders(crumb.item.driveId, crumb.item.id);
        }
        this.selectedLocation = null;
        this.clearSearchAndSelectionState();
      }, 'Navigation failed');
    },
    // Check if a file is currently selected
    isFileSelected(file) {
      return this.selectedFiles.some(selectedFile => selectedFile.id === file.id);
    },
    // Toggle selection of a single file
    toggleFileSelection(file) {
      const index = this.selectedFiles.findIndex(selectedFile => selectedFile.id === file.id);
      if (index > -1) {
        // File is selected, remove it
        this.selectedFiles.splice(index, 1);
      } else {
        // File is not selected, add it
        this.selectedFiles.push(file);
      }
    },
    /**
     * Toggle selection of all files
     * @param {boolean} selectAll - Whether to select all or deselect all
     */
    toggleSelectAll(selectAll) {
      const files = this.displayFiles;
      if (selectAll) {
        // Add any files that aren't already selected
        files.forEach(file => {
          if (!this.isFileSelected(file)) {
            this.selectedFiles.push(file);
          }
        });
      } else {
        // Remove all files from selection
        files.forEach(file => {
          const index = this.selectedFiles.findIndex(selectedFile => selectedFile.id === file.id);
          if (index > -1) {
            this.selectedFiles.splice(index, 1);
          }
        });
      }
    },
    // Handle file filtering from FileSearchFilter component
    onFilesFiltered(filterData) {
      // Only update if values have actually changed to prevent unnecessary re-renders
      if (this.filteredFiles !== filterData.filteredFiles) {
        this.filteredFiles = filterData.filteredFiles;
      }
      if (this.fileSearchActive !== filterData.hasActiveFilters) {
        this.fileSearchActive = filterData.hasActiveFilters;
      }
      if (this.isSiteSearch !== (filterData.isSiteSearch || false)) {
        this.isSiteSearch = filterData.isSiteSearch || false;
      }
      if (this.isSearching !== (filterData.isSearching || false)) {
        this.isSearching = filterData.isSearching || false;
      }
      
      // Merge search errors into the main errorMessage
      const newSearchError = filterData.searchError || null;
      if (this.searchError !== newSearchError) {
        this.searchError = newSearchError;
        // Update errorMessage to show search error if present, otherwise keep existing error
        if (newSearchError) {
          this.errorMessage = newSearchError;
        } else if (this.errorMessage === this.searchError) {
          // Clear errorMessage only if it was showing the previous search error
          this.errorMessage = '';
        }
      }
    },
    // Get the CSS class for item selection state
    getItemSelectionClass(item) {
      if (this.isSelectingFiles && item.type === 'file') {
        return this.isFileSelected(item) ? 'bg-primary text-white' : 'bg-light';
      }
      return this.selectedLocation && this.selectedLocation.id === item.id ? 'bg-primary text-white' : 'bg-light';
    },
    // Get the Font Awesome icon for item type
    getItemIcon(item) {
      // For files, determine icon based on file extension
      if (item.type === 'file') {
        return getFileTypeIcon(item.name || item.displayName);
      }
      // Icon configuration for other item types
      const iconMap = {
        site: ['fas', 'globe'],
        drive: ['fas', 'hdd'],
        folder: ['fas', 'folder'],
        file: ['fas', 'file'],
      };
      return iconMap[item.type] || iconMap.file;
    },
    // Get the CSS style colour for item icons
    getItemIconStyle(item) {
      if (item.type === 'file') {
        if (this.isSelectingFiles && this.isFileSelected(item)) {
          return;
        } else {
          return getFileTypeColour(item.name || item.displayName);
        }
      }
    },
    // Set item as the selected location or toggle file selection
    selectItem(item) {
      if (this.isSelectingFiles && item.type === 'file') {
        // In selecting file mode, toggle the file selection
        this.toggleFileSelection(item);
      } else {
        // Otherwise set the selected item as selected location
        this.selectedLocation = item;
      }
    },
    // Handle modal 'Select' button
    handleSelect() {
      if (this.isSelectingFiles && this.selectedFiles.length > 0) {
        // If selecting files, emit the selected files
        const files = this.formatSelectedFilesForEmission(this.selectedFiles);
        this.$emit('document-selected', files);
      } else if (this.selectedLocation) {
        // Single location selection (folder/drive/site)
        this.$emit('location-selected', {
          item: this.selectedLocation,
        });
      }
    },
  },
  async mounted() {
    this.loading = true;
    // Initialise with default location data if provided
    if (this.localInitialLocationData && this.localInitialLocationData.SPDefaultSiteId) {
      await this.loadSite(this.localInitialLocationData.SPDefaultSiteId);
    }
    this.loading = false;
  },
};
</script>

<style lang="scss" scoped>
.location-item {
  transition: background-color 0.2s;
  
  &:hover:not(.bg-primary) {
    background-color: #e2e6ea !important;
  }
}

.cursor-pointer {
  cursor: pointer;
}

::v-deep .breadcrumb {
  font-size: 0.75rem;
  padding: 0.5rem;
}

// Restrict modal height
::v-deep .modal-dialog {
  max-height: 80vh;
  
  .modal-content {
    max-height: 80vh;
    display: flex;
    flex-direction: column;
  }
  
  .modal-body {
    max-height: calc(80vh - 120px); // Account for header and footer
    overflow-y: auto;
    flex: 1;
  }
}

// Ensure the modal body is scrollable and has proper height
::v-deep .sharepoint-modal-body {
  max-height: calc(80vh - 120px);
  overflow-y: auto;
  padding: 1rem;
}

::v-deep .dropdown-menu {
  min-width: 8rem;
}

::v-deep .dropdown-item {
  padding: 0.25rem;
}
</style>